import { Controller } from "@hotwired/stimulus"

/**
 * The change-time sheet for a recurring occurrence lets staff move it to
 * another day. That move is a one-off by definition: "this and every one
 * after" regenerates the series from its recurrence rule, which has no way to
 * express "the third Tuesday goes to a Wednesday" — it would either drag every
 * future occurrence onto that date or quietly drop the change.
 *
 * So the moment the date differs from the one the sheet opened with, the
 * "this and future" option is disabled, "just this one" is selected, and a note
 * says why. Reverting the date puts the option back.
 *
 * The radios belong to the shared `event_instances/_scope_choice` partial, so
 * they are found by name rather than wired as targets — the partial is also
 * rendered by the cancel sheet, which has no date field to lock against.
 *
 * The server enforces the same rule (EventInstancesController#scope_param):
 * this only keeps the form from offering a choice it will not honour.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["date", "note"]
  static values = { originalDate: String }

  declare readonly hasDateTarget: boolean
  declare readonly dateTarget: HTMLInputElement
  declare readonly hasNoteTarget: boolean
  declare readonly noteTarget: HTMLElement
  declare originalDateValue: string

  connect() {
    this.dateChanged()
  }

  dateChanged() {
    const moved = this.hasDateTarget && this.dateTarget.value !== this.originalDateValue
    const future = this.futureRadio
    if (!future) return

    future.disabled = moved
    if (moved && future.checked) {
      const thisOnly = this.thisOnlyRadio
      if (thisOnly) thisOnly.checked = true
    }

    const label = future.closest("label")
    label?.classList.toggle("opacity-40", moved)
    label?.classList.toggle("cursor-not-allowed", moved)
    label?.classList.toggle("cursor-pointer", !moved)

    if (this.hasNoteTarget) this.noteTarget.classList.toggle("hidden", !moved)
  }

  private get futureRadio(): HTMLInputElement | null {
    return this.element.querySelector('input[name="scope"][value="this_and_future"]')
  }

  private get thisOnlyRadio(): HTMLInputElement | null {
    return this.element.querySelector('input[name="scope"][value="this_only"]')
  }
}
